import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    FiShield,
    FiArrowLeft,
    FiPlus,
    FiTrash2,
    FiMenu,
} from "react-icons/fi";
import {
    PageHeader,
    EmptyState,
    Skeleton,
    useToast,
} from "../../theme/components";
import { PageButton } from "../../theme/components/PageButton";
import { ConfirmDialog } from "../../theme/components/ConfirmDialog";
import { vaccinationsApi } from "../../api/endpoints/vaccinations";
import { parseApiError } from "../../api/errors";
import "./AdminVaccines.css";

// Curated age labels accepted by the backend's
// ``compute_scheduled_date`` helper. Free-form entry would also parse
// (any "N Months/Years Completed" string), but a select keeps things
// consistent with the existing vaccination rows.
const AGE_LABEL_OPTIONS = [
    "At Birth",
    "Newborn",
    "2 Months Completed",
    "4 Months Completed",
    "6 Months Completed",
    "9 Months Completed",
    "12 Months Completed",
    "15 Months Completed",
    "18 Months Completed",
    "2 Years Completed",
    "3 Years Completed",
    "4 Years Completed",
    "5 Years Completed",
];

const AdminVaccines = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [vaccines, setVaccines] = useState(null);
    const [error, setError] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newVaccine, setNewVaccine] = useState({
        vaccineName: "",
        ageLabel: "At Birth",
    });
    const [submitting, setSubmitting] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);

    // Drag-drop state.
    const [draggingIdx, setDraggingIdx] = useState(null);
    const [hoverIdx, setHoverIdx] = useState(null);
    const [hoverPos, setHoverPos] = useState(null); // "before" | "after"
    const [reordering, setReordering] = useState(false);
    const dragSnapshot = useRef(null);

    const load = useCallback(async () => {
        try {
            const list = await vaccinationsApi.catalogue();
            setVaccines(list);
            setError(null);
        } catch (e) {
            setError(parseApiError(e, "Failed to load vaccine catalogue."));
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleCreate = useCallback(
        async (e) => {
            e.preventDefault();
            const payload = {
                vaccineName: newVaccine.vaccineName.trim(),
                ageLabel: newVaccine.ageLabel.trim(),
            };
            if (!payload.vaccineName || !payload.ageLabel) {
                toast.error("Vaccine name and age label are both required.");
                return;
            }
            setSubmitting(true);
            try {
                const result = await vaccinationsApi.create(payload);
                toast.success(
                    `Added ${result.vaccine.vaccineName}. Backfilled ${result.backfilledChildren} child record(s).`,
                );
                setShowAddForm(false);
                setNewVaccine({ vaccineName: "", ageLabel: "At Birth" });
                await load();
            } catch (err) {
                toast.error(parseApiError(err, "Failed to add vaccine."));
            } finally {
                setSubmitting(false);
            }
        },
        [newVaccine, toast, load],
    );

    const handleConfirmDelete = useCallback(async () => {
        if (!pendingDelete) return;
        const v = pendingDelete;
        setPendingDelete(null);
        try {
            const result = await vaccinationsApi.deleteMaster(v.vaccineId);
            toast.success(
                `Removed ${v.vaccineName} and ${result.removedChildRecords} child record(s).`,
            );
            await load();
        } catch (err) {
            toast.error(parseApiError(err, "Failed to delete vaccine."));
        }
    }, [pendingDelete, toast, load]);

    // ------- drag-drop handlers (native HTML5) -------------------------

    const handleDragStart = useCallback((e, idx) => {
        dragSnapshot.current = vaccines ? [...vaccines] : null;
        setDraggingIdx(idx);
        e.dataTransfer.effectAllowed = "move";
        // Firefox needs data on the transfer to actually start the drag.
        e.dataTransfer.setData("text/plain", String(idx));
    }, [vaccines]);

    const handleDragOver = useCallback((e, idx) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        const rect = e.currentTarget.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        const pos = e.clientY < midY ? "before" : "after";
        if (hoverIdx !== idx || hoverPos !== pos) {
            setHoverIdx(idx);
            setHoverPos(pos);
        }
    }, [hoverIdx, hoverPos]);

    const handleDragLeave = useCallback((e) => {
        // Only clear when leaving the row itself, not its children.
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setHoverIdx(null);
            setHoverPos(null);
        }
    }, []);

    const handleDrop = useCallback(
        async (e, idx) => {
            e.preventDefault();
            const from = draggingIdx;
            const targetIdx = hoverPos === "after" ? idx + 1 : idx;
            setDraggingIdx(null);
            setHoverIdx(null);
            setHoverPos(null);
            if (from === null || from === targetIdx || from + 1 === targetIdx) return;

            const snapshot = dragSnapshot.current;
            if (!snapshot) return;
            const next = [...snapshot];
            const [moved] = next.splice(from, 1);
            const insertAt = from < targetIdx ? targetIdx - 1 : targetIdx;
            next.splice(insertAt, 0, moved);
            setVaccines(next);
            setReordering(true);
            try {
                await vaccinationsApi.reorder(next.map((v) => v.vaccineId));
                toast.success("Order saved.");
            } catch (err) {
                toast.error(parseApiError(err, "Failed to save new order."));
                setVaccines(snapshot); // rollback
            } finally {
                setReordering(false);
            }
        },
        [draggingIdx, hoverPos, toast],
    );

    const handleDragEnd = useCallback(() => {
        setDraggingIdx(null);
        setHoverIdx(null);
        setHoverPos(null);
    }, []);

    return (
        <div className="admin-vacc-wrapper">
            <PageHeader
                title="Vaccine Catalogue"
                subtitle="Drag to reorder — new entries auto-backfill to every active child."
                icon={FiShield}
                actions={
                    <PageButton
                        type="button"
                        variant="ghost"
                        startIcon={<FiArrowLeft size={16} />}
                        onClick={() => navigate("/dashboard")}
                    >
                        Back
                    </PageButton>
                }
            />

            <div className="admin-vacc-body">
                <div className="admin-vacc-toolbar">
                    <PageButton
                        type="button"
                        variant="primary"
                        startIcon={<FiPlus size={16} />}
                        onClick={() => setShowAddForm((s) => !s)}
                        disabled={showAddForm}
                    >
                        Add vaccine
                    </PageButton>
                </div>

                {showAddForm && (
                    <form className="admin-vacc-form" onSubmit={handleCreate}>
                        <div className="admin-vacc-form__grid">
                            <label className="admin-vacc-form__field">
                                <span>Vaccine name</span>
                                <input
                                    type="text"
                                    placeholder="e.g. Hepatitis A"
                                    value={newVaccine.vaccineName}
                                    onChange={(e) =>
                                        setNewVaccine((p) => ({
                                            ...p,
                                            vaccineName: e.target.value,
                                        }))
                                    }
                                    required
                                />
                            </label>
                            <label className="admin-vacc-form__field">
                                <span>Age label</span>
                                <select
                                    value={newVaccine.ageLabel}
                                    onChange={(e) =>
                                        setNewVaccine((p) => ({
                                            ...p,
                                            ageLabel: e.target.value,
                                        }))
                                    }
                                >
                                    {AGE_LABEL_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>
                                            {opt}
                                        </option>
                                    ))}
                                </select>
                                <small>
                                    New entry will be inserted after the last
                                    item with the same label, or before the
                                    next later one.
                                </small>
                            </label>
                        </div>
                        <div className="admin-vacc-form__actions">
                            <PageButton
                                type="button"
                                variant="ghost"
                                onClick={() => {
                                    setShowAddForm(false);
                                    setNewVaccine({
                                        vaccineName: "",
                                        ageLabel: "At Birth",
                                    });
                                }}
                                disabled={submitting}
                            >
                                Cancel
                            </PageButton>
                            <PageButton
                                type="submit"
                                variant="primary"
                                disabled={submitting}
                            >
                                {submitting ? "Saving…" : "Add vaccine"}
                            </PageButton>
                        </div>
                    </form>
                )}

                {error ? (
                    <EmptyState
                        illustration="error"
                        compact
                        title="Failed to load catalogue"
                        description={error}
                        action={
                            <PageButton
                                type="button"
                                variant="ghost"
                                onClick={load}
                            >
                                Retry
                            </PageButton>
                        }
                    />
                ) : vaccines === null ? (
                    <ul className="admin-vacc-dnd-list">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <li key={i} className="admin-vacc-dnd-row admin-vacc-dnd-row--skeleton">
                                <span className="admin-vacc-dnd-handle" />
                                <span className="admin-vacc-dnd-pos">
                                    <Skeleton width={16} height={16} variant="circular" />
                                </span>
                                <div className="admin-vacc-dnd-info">
                                    <Skeleton width="60%" height={18} />
                                    <Skeleton width="40%" height={14} />
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : vaccines.length === 0 ? (
                    <EmptyState
                        illustration="no-data"
                        compact
                        title="No vaccines yet"
                        description="Add the first one with the button above."
                    />
                ) : (
                    <ul
                        className={`admin-vacc-dnd-list${reordering ? " admin-vacc-dnd-list--saving" : ""}`}
                        aria-busy={reordering}
                    >
                        {vaccines.map((v, idx) => {
                            const isDragging = draggingIdx === idx;
                            const showBefore = hoverIdx === idx && hoverPos === "before" && draggingIdx !== null && draggingIdx !== idx;
                            const showAfter = hoverIdx === idx && hoverPos === "after" && draggingIdx !== null && draggingIdx !== idx;
                            return (
                                <li
                                    key={v.vaccineId}
                                    className={[
                                        "admin-vacc-dnd-row",
                                        isDragging ? "admin-vacc-dnd-row--dragging" : "",
                                        showBefore ? "admin-vacc-dnd-drop-before" : "",
                                        showAfter ? "admin-vacc-dnd-drop-after" : "",
                                    ].filter(Boolean).join(" ")}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, idx)}
                                    onDragOver={(e) => handleDragOver(e, idx)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, idx)}
                                    onDragEnd={handleDragEnd}
                                >
                                    <span
                                        className="admin-vacc-dnd-handle"
                                        aria-label="Drag to reorder"
                                        title="Drag to reorder"
                                    >
                                        <FiMenu size={18} />
                                    </span>
                                    <span className="admin-vacc-dnd-pos">{idx + 1}</span>
                                    <div className="admin-vacc-dnd-info">
                                        <span className="admin-vacc-dnd-name">
                                            {v.vaccineName}
                                        </span>
                                        <span className="admin-vacc-dnd-age">
                                            {v.ageLabel}
                                        </span>
                                    </div>
                                    <PageButton
                                        type="button"
                                        size="small"
                                        variant="ghost"
                                        startIcon={<FiTrash2 size={14} />}
                                        onClick={() => setPendingDelete(v)}
                                        title="Delete vaccine"
                                        sx={{
                                            color: "var(--pt-error)",
                                            borderColor:
                                                "color-mix(in srgb, var(--pt-error) 30%, transparent)",
                                        }}
                                    >
                                        Delete
                                    </PageButton>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {pendingDelete && (
                <ConfirmDialog
                    open
                    title={`Delete ${pendingDelete.vaccineName}?`}
                    body={
                        <div>
                            <p>
                                This removes the vaccine from the master
                                catalogue and <strong>cascades</strong> to
                                delete the per-child records for every child
                                in PHM. This cannot be undone.
                            </p>
                        </div>
                    }
                    confirmLabel="Delete"
                    confirmSeverity="error"
                    onConfirm={handleConfirmDelete}
                    onClose={() => setPendingDelete(null)}
                />
            )}
        </div>
    );
};

export default AdminVaccines;